import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Phone, FileText, Globe, Calendar, Package, MessageSquare, ClipboardList, TrendingUp, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import UserProfileDropdown from './auth/UserProfileDropdown';
import { APP_NAME } from '../config/branding';
import { trackGetStarted, trackAppDownload } from '../utils/analytics';

const Header = () => {
  const { isAuthenticated, username, user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const isSystemAdmin = user?.role === 'SYSTEM_ADMIN';
  const isAdmin = user?.role === 'ADMIN';
  const isVetStaff = user?.role === 'VET_STAFF' || user?.role === 'PRACTICE_ADMIN' || user?.role === 'SYSTEM_ADMIN';
  const isPendingInvite = user?.role === 'PENDING_INVITE';

  return (
    <>
      <header className="bg-white dark:bg-dark-bg-secondary border-b border-gray-100 dark:border-dark-border sticky top-0 z-50">
        <div className="w-full px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Brand with Navigation */}
            <div className="flex items-center space-x-8">
              <div className="flex items-center space-x-3">
                <img 
                  src="/logo_clear_back.png" 
                  alt={`${APP_NAME} Logo`} 
                  width="36" 
                  height="36" 
                  className="object-contain"
                />
                <Link to={isVetStaff ? "/dashboard" : "/"} className="text-xl font-medium text-gray-900 dark:text-dark-text-primary hover:text-gray-700 dark:hover:text-dark-text-secondary transition-colors" style={{
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, "Apple Color Emoji", Arial, sans-serif, "Segoe UI Emoji", "Segoe UI Symbol"',
                  letterSpacing: '-0.01em',
                  fontWeight: '500'
                }}>
                  {APP_NAME}
                </Link>
              </div>

              {/* Desktop Navigation - Left Side */}
              <div className="hidden md:flex items-center space-x-6">
              {!isAuthenticated && (
                <>
                  {/* Products Dropdown */}
                  <div 
                    className="relative"
                    onMouseEnter={() => setIsProductsOpen(true)}
                    onMouseLeave={() => setIsProductsOpen(false)}
                  >
                    <button className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors py-2">
                      Products
                    </button>
                    
                    {/* Products Dropdown Menu */}
                    {isProductsOpen && (
                      <div className="absolute top-full left-[-90px] pt-2 z-50">
                        {/* Upward pointing caret */}
                        <div className="absolute top-1 left-[166px] w-3 h-3 bg-white dark:bg-dark-bg-secondary border-l border-t border-gray-100 dark:border-dark-border transform rotate-45"></div>
                        <div className="w-[550px] bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl border border-gray-100 dark:border-dark-border py-4 px-4">
                          {/* 2x2 Grid */}
                          <div className="grid grid-cols-2 gap-3">
                            {/* Calendar */}
                            <Link 
                              to="/products/calendar" 
                              className="flex flex-col gap-3 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors group"
                              onClick={() => setIsProductsOpen(false)}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center group-hover:bg-orange-200 dark:group-hover:bg-orange-900/50 transition-colors">
                                    <Calendar className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                                  </div>
                                </div>
                                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Calendar</h3>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Visual scheduling with swim lanes and real-time patient flow tracking for your clinic.</p>
                            </Link>

                            {/* Online Booking */}
                            <Link 
                              to="/products/online-booking" 
                              className="flex flex-col gap-3 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors group"
                              onClick={() => setIsProductsOpen(false)}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                                    <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                  </div>
                                </div>
                                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Online Booking</h3>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Embeddable appointment booking widget for your website with real-time availability.</p>
                            </Link>

                            {/* Mobile Forms */}
                            <Link 
                              to="/products/mobile-forms" 
                              className="flex flex-col gap-3 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors group"
                              onClick={() => setIsProductsOpen(false)}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-10 h-10 bg-rose-100 dark:bg-rose-900/30 rounded-lg flex items-center justify-center group-hover:bg-rose-200 dark:group-hover:bg-rose-900/50 transition-colors">
                                    <ClipboardList className="w-5 h-5 text-rose-600 dark:text-rose-400" />
                                  </div>
                                </div>
                                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Mobile Forms</h3>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Digital check-in forms that clients complete on their phone before arrival.</p>
                            </Link>

                            {/* 2-Way Texting */}
                            <Link 
                              to="/products/two-way-texting" 
                              className="flex flex-col gap-3 p-4 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors group"
                              onClick={() => setIsProductsOpen(false)}
                            >
                              <div className="flex items-start gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-lg flex items-center justify-center group-hover:bg-teal-200 dark:group-hover:bg-teal-900/50 transition-colors">
                                    <MessageSquare className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                                  </div>
                                </div>
                                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">2-Way Texting</h3>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Send and receive SMS & MMS messages with clients directly from your dashboard.</p>
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Solutions Dropdown */}
                  <div 
                    className="relative"
                    onMouseEnter={() => setIsSolutionsOpen(true)}
                    onMouseLeave={() => setIsSolutionsOpen(false)}
                  >
                    <button className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors py-2">
                      Solutions
                    </button>
                    
                    {/* Solutions Dropdown Menu */}
                    {isSolutionsOpen && (
                      <div className="absolute top-full left-[-40px] pt-2 z-50">
                        {/* Upward pointing caret */}
                        <div className="absolute top-1 left-[80px] w-3 h-3 bg-white dark:bg-dark-bg-secondary border-l border-t border-gray-100 dark:border-dark-border transform rotate-45"></div>
                        <div className="w-[320px] bg-white dark:bg-dark-bg-secondary rounded-lg shadow-xl border border-gray-100 dark:border-dark-border py-4 px-4">
                          <div className="space-y-2">
                            {/* Grow Revenue */}
                            <Link 
                              to="/solutions/grow" 
                              className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors group"
                              onClick={() => setIsSolutionsOpen(false)}
                            >
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center group-hover:bg-green-200 dark:group-hover:bg-green-900/50 transition-colors">
                                  <TrendingUp className="w-5 h-5 text-green-600 dark:text-green-400" />
                                </div>
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Grow Revenue</h3>
                                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Vaccine reminders & upsell opportunities</p>
                              </div>
                            </Link>

                            {/* Recover Revenue */}
                            <Link 
                              to="/solutions/recover" 
                              className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-bg-hover transition-colors group"
                              onClick={() => setIsSolutionsOpen(false)}
                            >
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center group-hover:bg-blue-200 dark:group-hover:bg-blue-900/50 transition-colors">
                                  <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900 dark:text-dark-text-primary">Recover Revenue</h3>
                                <p className="text-sm text-gray-600 dark:text-dark-text-secondary">Reduce no-shows by 15%</p>
                              </div>
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <Link to="/contact" className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors">Contact</Link>
                  <Link to="/pricing" className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors">Pricing</Link>
                </>
              )}
              {isAuthenticated && (
                <>
                  {isPendingInvite ? (
                    <>
                      {/* For PENDING_INVITE users, show link to invitations page */}
                      <Link to="/pending-invitations" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium transition-colors">My Invitations</Link>
                    </>
                  ) : (
                    <>
                      {/* For active staff/admin users, show normal navigation */}
                      {isVetStaff && (
                        <Link to="/dashboard" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium transition-colors">Dashboard</Link>
                      )}
                      {isSystemAdmin && (
                        <Link to="/practices" className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors">Practices</Link>
                      )}
                    </>
                  )}
                </>
              )}
              </div>
            </div>

            {/* Right Side - Auth Buttons */}
            <div className="hidden md:flex items-center space-x-4">
              {isAuthenticated ? (
                <UserProfileDropdown />
              ) : (
                <>
                  <Link to="/docs" className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors">Docs</Link>
                  <Link to="/login" className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors">Log in</Link>
                  <Link 
                    to="/signup" 
                    className="bg-gray-900 dark:bg-blue-600 text-white px-4 py-2 rounded font-medium hover:bg-gray-800 dark:hover:bg-blue-700 transition-colors"
                    onClick={() => trackGetStarted('header')}
                  >
                    Sign up
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 rounded-md text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-100 dark:border-dark-border pt-4">
              <div className="flex flex-col space-y-4">

                
                {!isAuthenticated && (
                  <>
                    <Link 
                      to="/" 
                      className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Home
                    </Link>
                    <Link 
                      to="/solutions/grow" 
                      className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Grow Revenue
                    </Link>
                    <Link 
                      to="/solutions/recover" 
                      className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Recover Revenue
                    </Link>
                    <Link 
                      to="/contact" 
                      className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Contact
                    </Link>
                    <Link 
                      to="/pricing" 
                      className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Pricing
                    </Link>
                    <Link 
                      to="/docs" 
                      className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Docs
                    </Link>
                    <Link 
                      to="/login" 
                      className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Log in
                    </Link>
                    <Link 
                      to="/signup" 
                      className="bg-gray-900 dark:bg-blue-600 text-white px-4 py-2 rounded hover:bg-gray-800 dark:hover:bg-blue-700 font-medium transition-colors text-center"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        trackGetStarted('mobile_menu');
                      }}
                    >
                      Sign up
                    </Link>
                  </>
                )}

                {isAuthenticated && (
                  <>
                    {isPendingInvite ? (
                      <>
                        {/* For PENDING_INVITE users, show link to invitations page */}
                        <Link 
                          to="/pending-invitations" 
                          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium transition-colors text-center"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          My Invitations
                        </Link>
                      </>
                    ) : (
                      <>
                        {/* For active staff/admin users, show normal navigation */}
                        {isVetStaff && (
                          <Link 
                            to="/dashboard" 
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium transition-colors text-center"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Dashboard
                          </Link>
                        )}
                        {isAdmin && (
                          <Link 
                            to="/practices" 
                            className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Practices
                          </Link>
                        )}
                        {isVetStaff && (
                          <Link 
                            to="/practice/team" 
                            className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                            onClick={() => setIsMobileMenuOpen(false)}
                          >
                            Team
                          </Link>
                        )}
                        <Link 
                          to="/rag/search" 
                          className="text-gray-600 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary font-medium transition-colors"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          Search
                        </Link>
                      </>
                    )}
                    
                    {/* Mobile User Profile Section */}
                    <div className="pt-4 border-t border-gray-100 dark:border-dark-border">
                      <div className="flex items-center space-x-3 mb-3">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm font-medium">
                            {username?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-gray-900 dark:text-dark-text-primary font-medium">{username}</span>
                      </div>
                      <button
                        onClick={() => {
                          logout();
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full text-left text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-medium transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
